(() => {
  'use strict';

  const requiredSourcePattern = /Please fill in all required fields marked with \*\.?/gi;
  const requiredTranslatedMessage = 'Por favor, completa todos los campos obligatorios marcados con *.';
  const wrapItSelector = [
    '[class*="wrapit" i]',
    '[id*="wrapit" i]',
    '[data-wrapit]',
    '[data-app*="wrapit" i]',
    '[class*="gift-wrap" i][class*="app" i]',
    '[id*="gift-wrap" i][id*="app" i]'
  ].join(',');

  function translateRequiredMessage(value) {
    if (typeof value !== 'string') return value;
    return value.replace(requiredSourcePattern, requiredTranslatedMessage);
  }

  function isInsideWrapIt(element) {
    return Boolean(element?.closest?.(wrapItSelector));
  }

  function fieldRole(element) {
    if (!(element instanceof Element)) return null;

    const signature = [
      element.getAttribute('name'),
      element.id,
      element.getAttribute('data-field'),
      element.getAttribute('data-name'),
      element.getAttribute('data-testid'),
      element.getAttribute('aria-label'),
      element.getAttribute('autocomplete')
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (/(^|[^a-z])(from|sender|remitente)([^a-z]|$)/.test(signature)) return 'from';
    if (/(^|[^a-z])(to|recipient|destinatari[ao])([^a-z]|$)/.test(signature)) return 'to';
    return null;
  }

  function correctedPlaceholder(element, currentValue) {
    if (typeof currentValue !== 'string') return currentValue;

    const role = fieldRole(element);
    if (role === 'from') return 'Remitente';
    if (role === 'to') return 'Destinataria';

    if (!isInsideWrapIt(element)) return currentValue;

    const normalized = currentValue.trim().replace(/:$/, '').toLowerCase();
    if (normalized === 'destinataria') return 'Remitente';
    if (normalized === 'remitente') return 'Destinataria';
    if (normalized === 'from') return 'Remitente';
    if (normalized === 'to') return 'Destinataria';

    return currentValue;
  }

  function correctedLabel(value, element) {
    const requiredTranslation = translateRequiredMessage(value);
    if (requiredTranslation !== value) return requiredTranslation;
    if (!isInsideWrapIt(element) || typeof value !== 'string') return value;

    const normalized = value.trim().replace(/:$/, '').toLowerCase();
    if (normalized === 'from') return value.replace(/from/i, 'Remitente');
    if (normalized === 'to') return value.replace(/to/i, 'Destinataria');
    return value;
  }

  const nativeAlert = window.alert;
  window.alert = function jd1TranslatedAlert(message) {
    return nativeAlert.call(window, translateRequiredMessage(String(message)));
  };

  const nativeConfirm = window.confirm;
  window.confirm = function jd1TranslatedConfirm(message) {
    return nativeConfirm.call(window, translateRequiredMessage(String(message)));
  };

  ['HTMLInputElement', 'HTMLTextAreaElement', 'HTMLSelectElement', 'HTMLButtonElement'].forEach((constructorName) => {
    const prototype = window[constructorName]?.prototype;
    if (!prototype || typeof prototype.setCustomValidity !== 'function') return;

    const nativeSetCustomValidity = prototype.setCustomValidity;
    prototype.setCustomValidity = function jd1TranslatedValidity(message) {
      return nativeSetCustomValidity.call(this, translateRequiredMessage(String(message)));
    };
  });

  const translatedAttributes = ['aria-label', 'title', 'data-error', 'data-message', 'data-validation-message'];

  function translateElement(element) {
    if (!(element instanceof Element)) return;

    translatedAttributes.forEach((attribute) => {
      if (!element.hasAttribute(attribute)) return;
      const currentValue = element.getAttribute(attribute);
      let nextValue = translateRequiredMessage(currentValue);

      if (attribute === 'aria-label') {
        const role = fieldRole(element);
        if (role === 'from') nextValue = 'Remitente';
        if (role === 'to') nextValue = 'Destinataria';
      }

      if (nextValue !== currentValue) element.setAttribute(attribute, nextValue);
    });

    if (element.hasAttribute('placeholder')) {
      const currentPlaceholder = element.getAttribute('placeholder');
      const nextPlaceholder = correctedPlaceholder(element, currentPlaceholder);
      if (nextPlaceholder !== currentPlaceholder) element.setAttribute('placeholder', nextPlaceholder);
    }
  }

  function translateTextNode(node) {
    if (!(node instanceof Text)) return;
    const parent = node.parentElement;
    if (!parent || ['SCRIPT', 'STYLE'].includes(parent.tagName)) return;

    const currentValue = node.nodeValue || '';
    const nextValue = correctedLabel(currentValue, parent);
    if (nextValue !== currentValue) node.nodeValue = nextValue;
  }

  function translateTree(root) {
    if (!root) return;

    if (root.nodeType === Node.TEXT_NODE) {
      translateTextNode(root);
      return;
    }

    if (![Node.ELEMENT_NODE, Node.DOCUMENT_NODE, Node.DOCUMENT_FRAGMENT_NODE].includes(root.nodeType)) return;

    if (root.nodeType === Node.ELEMENT_NODE) translateElement(root);

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      if (node.nodeType === Node.TEXT_NODE) translateTextNode(node);
      else translateElement(node);
      node = walker.nextNode();
    }
  }

  function init() {
    translateTree(document.body);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          translateTextNode(mutation.target);
          return;
        }

        if (mutation.type === 'attributes') {
          translateElement(mutation.target);
          return;
        }

        mutation.addedNodes.forEach(translateTree);
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: [
        'placeholder',
        'aria-label',
        'title',
        'data-error',
        'data-message',
        'data-validation-message'
      ]
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
